import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_TOLERANCE_PX = 1;

export type ScrollAffordance = {
  scrollRef: React.RefObject<HTMLDivElement>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  handleScroll: (direction: 'left' | 'right') => void;
};

type UseScrollAffordanceArgs = {
  rowsLength: number;
  rowLabelColumnWidth: number;
};

/**
 * Given each column's left offset within the scroll content and the current
 * scrollLeft, return the next/previous scroll position that aligns a column
 * cleanly to the right edge of the sticky labels column. Returns `null` when
 * scrolling right past the last snap point, and `0` when scrolling left from
 * the first snap point.
 */
export const computeNextScrollTarget = (
  columnOffsets: ReadonlyArray<number>,
  scrollLeft: number,
  direction: 'left' | 'right',
): number | null => {
  if (direction === 'right') {
    return columnOffsets.find((t) => t > scrollLeft + SCROLL_TOLERANCE_PX) ?? null;
  }
  return [...columnOffsets].reverse().find((t) => t < scrollLeft - SCROLL_TOLERANCE_PX) ?? 0;
};

export const useScrollAffordance = ({ rowsLength, rowLabelColumnWidth }: UseScrollAffordanceArgs): ScrollAffordance => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollAffordance = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - SCROLL_TOLERANCE_PX);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollAffordance();

    let rafId: number | null = null;
    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateScrollAffordance();
      });
    };
    el.addEventListener('scroll', scheduleUpdate, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleUpdate) : null;
    observer?.observe(el);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', scheduleUpdate);
      observer?.disconnect();
    };
  }, [updateScrollAffordance, rowsLength]);

  const handleScroll = useCallback(
    (direction: 'left' | 'right') => {
      const el = scrollRef.current;
      if (!el) return;

      const headerCells = Array.from(el.querySelectorAll('thead > tr > th')) as Array<HTMLElement>;
      if (headerCells.length < 2) return;

      const containerLeft = el.getBoundingClientRect().left;
      const columnOffsets = headerCells
        .slice(1)
        .map((cell) =>
          Math.max(0, cell.getBoundingClientRect().left - containerLeft + el.scrollLeft - rowLabelColumnWidth),
        );

      const target = computeNextScrollTarget(columnOffsets, el.scrollLeft, direction);
      if (target == null) return;

      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollTo({ left: target, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    },
    [rowLabelColumnWidth],
  );

  return { scrollRef, canScrollLeft, canScrollRight, handleScroll };
};
